import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { dashboardAPI } from '../../services/api';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ClockIcon,
  LightningIcon,
  TrendUpIcon,
  ArrowUpRightIcon,
  PlusIcon,
  SparkleIcon,
  FolderIcon,
  UsersIcon,
  CheckIcon,
  ChartIcon,
} from '../icons';

interface DashboardData {
  summary: {
    total_projects: number;
    active_projects: number;
    pending_time_entries: number;
    total_hours: number;
    sred_hours: number;
    cdae_hours: number;
    billable_hours: number;
    team_members: number;
    developers_count: number;
  };
  trends: {
    projects_change_percent: number;
    team_members_change: number;
    total_hours_change_percent: number;
    billable_hours_change_percent: number;
  };
  weekly_data: { day: string; hours: number; sred: number }[];
  monthly_data: { month: string; hours: number; target: number }[];
  sred_projects: {
    id: number;
    name: string;
    total_hours: number;
    sred_hours: number;
    budget_hours: number | null;
    status: string;
    color: string;
  }[];
  chart_summary: {
    total_hours_this_period: number;
    period_change_percent: number;
  };
}

// Icons
const Icons = {
  clock: <ClockIcon className="w-5 h-5" strokeWidth={1.5} />,
  lightning: <LightningIcon className="w-5 h-5" strokeWidth={1.5} />,
  trendUp: <TrendUpIcon className="w-4 h-4" strokeWidth={2} />,
  arrowUpRight: <ArrowUpRightIcon className="w-4 h-4" strokeWidth={1.5} />,
  plus: <PlusIcon className="w-5 h-5" strokeWidth={2} />,
  sparkle: <SparkleIcon className="w-5 h-5" strokeWidth={1.5} />,
  folder: <FolderIcon className="w-5 h-5" strokeWidth={1.5} />,
  users: <UsersIcon className="w-5 h-5" strokeWidth={1.5} />,
  check: <CheckIcon className="w-5 h-5" strokeWidth={1.5} />,
  chart: <ChartIcon className="w-5 h-5" strokeWidth={1.5} />,
};

const TrendBadge: React.FC<{ value: number; isCount?: boolean }> = ({
  value,
  isCount,
}) => {
  if (value === 0) return null;
  const isPositive = value > 0;
  const display = isCount
    ? `${isPositive ? '+' : ''}${value}`
    : `${isPositive ? '+' : ''}${value}%`;
  return (
    <span
      className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
        isPositive
          ? 'text-emerald-500 bg-emerald-500/10'
          : 'text-rose-500 bg-rose-500/10'
      }`}
    >
      {display}
    </span>
  );
};

const ManagerOverview: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState<'weekly' | 'monthly'>(
    'weekly',
  );

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await dashboardAPI.getStats();
        setDashboard(response.data.dashboard);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className={`rounded-lg px-3 py-2 text-sm shadow-xl backdrop-blur-sm ${
            isDark
              ? 'bg-slate-900/90 border border-slate-700'
              : 'bg-white/90 border border-slate-200'
          }`}
        >
          <p
            className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {label}
          </p>
          {payload.map((entry: any, index: number) => (
            <p
              key={index}
              className={isDark ? 'text-slate-300' : 'text-slate-600'}
            >
              {entry.name}:{' '}
              <span className="font-semibold" style={{ color: entry.color }}>
                {entry.value}h
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading || !dashboard) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { summary, trends, weekly_data, monthly_data, sred_projects, chart_summary } = dashboard;

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 rounded-2xl'
    : 'bg-white border border-slate-200 rounded-2xl shadow-sm';

  return (
    <div className="space-y-6 p-6">
      {/* Ambient glow effect */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-600/8 rounded-full blur-[120px]" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1
          className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          Dashboard
        </h1>
        <Link
          to="/timesheet"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
        >
          {Icons.plus}
          <span>Log Time</span>
        </Link>
      </div>

      {/* Top Row: Tax Credits + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Tax Credits Highlight Card (SR&ED + CDAE) */}
        <div
          className="lg:col-span-8 relative overflow-hidden rounded-2xl p-6"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, rgb(21, 16, 72) 80%, rgb(49, 46, 129) 95%, rgb(76, 29, 149) 100%)'
              : '',
            border: isDark ? '' : '1px solid rgba(139, 92, 246, 0.2)',
          }}
        >
          {isDark && (
            <>
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-violet-950/30 to-purple-500/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-1/2 w-32 h-32 bg-gradient-to-br from-violet-500/20 to-violet-950 s0/10 rounded-full blur-2xl" />
            </>
          )}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div
                  className={`hidden sm:flex w-10 h-10 rounded-xl items-center justify-center ${isDark ? 'bg-violet-500/20' : 'bg-violet-500/10'}`}
                >
                  <svg
                    className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                </div>
                <div>
                  <h3
                    className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    {t('manager.taxCreditsThisPeriod')}
                  </h3>
                  <p
                    className={`text-xs sm:text-sm ${isDark ? 'text-violet-300' : 'text-violet-600'}`}
                  >
                    {t('manager.taxCreditEligible')}
                  </p>
                </div>
              </div>
              <Link
                to="/manager/reports"
                className={`text-sm font-medium ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
              >
                {t('common.viewAll')}
              </Link>
            </div>

            {/* Two Programs Side by Side */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5">
              {/* SR&ED Section */}
              <div
                className={`p-3 sm:p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/50'}`}
              >
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <div className={`w-2 h-2 rounded-full bg-violet-500`} />
                  <span
                    className={`text-xs sm:text-sm font-medium ${isDark ? 'text-violet-300' : 'text-violet-700'}`}
                  >
                    SR&ED
                  </span>
                </div>
                <p
                  className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {summary.sred_hours.toFixed(1)}h
                </p>
              </div>

              {/* CDAE Section */}
              <div
                className={`p-3 sm:p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/50'}`}
              >
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <div className={`w-2 h-2 rounded-full bg-cyan-500`} />
                  <span
                    className={`text-xs sm:text-sm font-medium ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}
                  >
                    CDAE
                  </span>
                </div>
                <p
                  className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {summary.cdae_hours.toFixed(1)}h
                </p>
              </div>
            </div>

            {/* Combined Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  {t('manager.eligibleVsTotal')}
                </span>
                <span
                  className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {(summary.sred_hours + summary.cdae_hours).toFixed(0)}
                  h / {summary.total_hours.toFixed(0)}h
                </span>
              </div>
              <div
                className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-slate-900/50' : 'bg-white-200/50'} flex`}
              >
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-violet-600"
                  style={{
                    width: `${summary.total_hours > 0 ? Math.min((summary.sred_hours / summary.total_hours) * 100, 100) : 0}%`,
                  }}
                />
                <div
                  className="h-full bg-gradient-to-r from-cyan-400 to-cyan-500"
                  style={{
                    width: `${summary.total_hours > 0 ? Math.min((summary.cdae_hours / summary.total_hours) * 100, 100) : 0}%`,
                  }}
                />
              </div>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-violet-500" />
                  <span
                    className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    SR&ED
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-cyan-500" />
                  <span
                    className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    CDAE
                  </span>
                </div>
              </div>
            </div>

            {/* Total Estimated Credit */}
            <div
              className={`p-4 rounded-xl ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm font-medium ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}
                >
                  {t('manager.totalEstimatedCredit')}
                </span>
                <span
                  className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                >
                  $
                  {(
                    summary.sred_hours * 45 +
                    summary.cdae_hours * 30
                  ).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions Panel */}
        <div className={`lg:col-span-4 ${cardClass} p-6 flex flex-col`}>
          <div className="flex items-center gap-3 mb-5">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-500/10'}`}
            >
              <svg
                className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
            </div>
            <h3
              className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('manager.quickActions')}
            </h3>
          </div>

          <div className="flex flex-col gap-3 flex-1">
            <Link
              to="/timesheet"
              className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                isDark
                  ? 'bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20'
                  : 'bg-violet-50 hover:bg-violet-100 border border-violet-200'
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center text-white">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
              </div>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.logTime')}</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.addNewEntry')}</p>
              </div>
            </Link>

            <Link
              to="/manager/approvals"
              className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                isDark
                  ? 'bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20'
                  : 'bg-amber-50 hover:bg-amber-100 border border-amber-200'
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              </div>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.approveEntries')}</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{summary.pending_time_entries} {t('manager.pendingReview')}</p>
              </div>
            </Link>

            <Link
              to="/manager/projects"
              className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                isDark
                  ? 'bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20'
                  : 'bg-blue-50 hover:bg-blue-100 border border-blue-200'
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path><line x1="12" y1="11" x2="12" y2="17"></line><line x1="9" y1="14" x2="15" y2="14"></line></svg>
              </div>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.createProject')}</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.startNewProject')}</p>
              </div>
            </Link>

            <Link
              to="/manager/reports"
              className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                isDark
                  ? 'bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20'
                  : 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
              </div>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.generateReport')}</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.sredCdaeReports')}</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Row: 4 Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {/* Projects (Combined) */}
        <div className={`${cardClass} p-5`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.projects')}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{summary.total_projects}</p>
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>({summary.active_projects} {t('manager.active')})</span>
              </div>
              <div className="flex items-center gap-1 mt-2">
                <TrendBadge value={trends.projects_change_percent} />
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
              {Icons.folder}
            </div>
          </div>
        </div>

        {/* Team Members */}
        <div className={`${cardClass} p-5`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.teamMembers')}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{summary.team_members}</p>
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>({summary.developers_count} {t('manager.developers')})</span>
              </div>
              <div className="flex items-center gap-1 mt-2">
                <TrendBadge value={trends.team_members_change} isCount />
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center text-white">
              {Icons.users}
            </div>
          </div>
        </div>

        {/* Total Hours */}
        <div className={`${cardClass} p-5`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.totalHours')}</p>
              <div className="flex items-baseline gap-1 mt-1">
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{summary.total_hours.toFixed(1)}h</p>
              </div>
              <div className="flex items-center gap-1 mt-2">
                <TrendBadge value={trends.total_hours_change_percent} />
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center text-white">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
            </div>
          </div>
        </div>

        {/* Billable Hours */}
        <div className={`${cardClass} p-5`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.billableHours')}</p>
              <div className="flex items-baseline gap-1 mt-1">
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{summary.billable_hours.toFixed(1)}h</p>
              </div>
              <div className="flex items-center gap-1 mt-2">
                <TrendBadge value={trends.billable_hours_change_percent} />
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
            </div>
          </div>
        </div>
      </div>

      {/* Main Chart Row */}
      <div className="grid grid-cols-12 gap-4">
        {/* Main Chart */}
        <div className={`col-span-12 lg:col-span-8 ${cardClass} p-6`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.totalHours')}</h2>
              <div className="flex items-baseline gap-2 mt-1">
                <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{chart_summary.total_hours_this_period}h</span>
                <TrendBadge value={chart_summary.period_change_percent} />
              </div>
            </div>
            <select
              value={chartPeriod}
              onChange={(e) =>
                setChartPeriod(e.target.value as 'weekly' | 'monthly')
              }
              className={`text-sm font-medium rounded-lg px-3 py-1.5 ${
                isDark
                  ? 'bg-slate-700/50 text-slate-300 border border-slate-600'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartPeriod === 'weekly' ? weekly_data : monthly_data}
              >
                <defs>
                  <linearGradient id="gradientMain" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient
                    id="gradientSecondary"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={isDark ? '#334155' : '#e2e8f0'}
                  vertical={false}
                />
                <XAxis
                  dataKey={chartPeriod === 'weekly' ? 'day' : 'month'}
                  stroke={isDark ? '#64748b' : '#94a3b8'}
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke={isDark ? '#64748b' : '#94a3b8'}
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${v}h`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="hours"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fill="url(#gradientMain)"
                  name="Total"
                />
                <Area
                  type="monotone"
                  dataKey={chartPeriod === 'weekly' ? 'sred' : 'target'}
                  stroke="#a78bfa"
                  strokeWidth={2}
                  fill="url(#gradientSecondary)"
                  name={chartPeriod === 'weekly' ? 'SR&ED' : 'Target'}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className={`col-span-12 lg:col-span-4 ${cardClass} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.recentActivity')}</h2>
            <Link
              to="/manager/activity"
              className={`text-sm font-medium ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
            >
              {t('common.viewAll')}
            </Link>
          </div>
          <div className="space-y-3">
            {/* Pending Time Entries */}
            <Link
              to="/manager/approvals"
              className={`flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02] ${isDark ? 'bg-blue-500/10 hover:bg-blue-500/20' : 'bg-blue-50 hover:bg-blue-100'}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                <svg className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.newTimeEntriesPending')}</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{summary.pending_time_entries} {t('manager.entriesWaitingReview')}</p>
              </div>
              <span className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{t('manager.today')}</span>
            </Link>

            {/* Project Milestone */}
            <Link
              to="/manager/projects"
              className={`flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02] ${isDark ? 'bg-emerald-500/10 hover:bg-emerald-500/20' : 'bg-emerald-50 hover:bg-emerald-100'}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                <svg className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.projectCompletedMilestone')}</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.sprintDeliverablesApproved')}</p>
              </div>
              <span className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{t('manager.oneDayAgo')}</span>
            </Link>

            {/* User Invitation */}
            <Link
              to="/manager/users"
              className={`flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02] ${isDark ? 'bg-amber-500/10 hover:bg-amber-500/20' : 'bg-amber-50 hover:bg-amber-100'}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                <svg className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.newUserInvitationSent')}</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.seniorDeveloperAwaiting')}</p>
              </div>
              <span className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{t('manager.twoDaysAgo')}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-12 gap-4">
        {/* AI Card */}
        <div className="col-span-12 lg:col-span-3">
          <div
            className={`relative overflow-hidden rounded-2xl p-5 h-full ${isDark ? '' : ' border border-slate-200'}`}
            style={
              isDark
                ? {
                    background:
                      'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
                  }
                : {}
            }
          >
            <div className="relative z-10">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center mb-4">
                <span className={` ${isDark ? 'text-white' : 'text-slate-900'}`}>{Icons.sparkle}</span>
              </div>
              <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Analyze with AI</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Get accurate SR&ED insights</p>
              <Link
                to="/ai-assistant"
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${isDark ? 'bg-white text-violet-900 hover:bg-violet-50' : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white'}`}
              >
                {Icons.plus}
                {t('manager.tryAiNow')}
              </Link>
            </div>
          </div>
        </div>

        {/* SR&ED Progress by Project */}
        <div className={`col-span-12 lg:col-span-9 ${cardClass} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                <svg className={`w-4 h-4 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></svg>
              </div>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t('manager.sredProgressByProject')}</h2>
            </div>
            <Link
              to="/manager/projects"
              className={`text-sm font-medium ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
            >
              {t('common.viewAll')}
            </Link>
          </div>
          {sred_projects.length === 0 ? (
            <div className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              <p className="text-sm">No SR&ED eligible projects found.</p>
              <Link to="/manager/projects" className={`text-sm font-medium mt-2 inline-block ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                Create a project
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {sred_projects.map((project) => {
                const sredPercent = project.total_hours > 0 ? Math.round((project.sred_hours / project.total_hours) * 100) : 0;
                const budgetPercent = project.budget_hours ? Math.round((project.total_hours / project.budget_hours) * 100) : null;
                return (
                  <div
                    key={project.id}
                    className={`p-4 rounded-xl ${isDark ? 'bg-slate-900/50' : 'bg-slate-50'} hover:scale-[1.02] transition-transform cursor-pointer`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: project.color }}
                      />
                      <span className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{project.name}</span>
                    </div>

                    {/* SR&ED Eligible */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('manager.sredEligible')}</span>
                        <span className={`text-xs font-medium ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>{sredPercent}%</span>
                      </div>
                      <div className={`h-1.5 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-violet-600"
                          style={{ width: `${sredPercent}%` }}
                        />
                      </div>
                    </div>

                    {/* Hours & Budget */}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{project.sred_hours}h</span>
                        <span className={`text-xs ml-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>/ {project.total_hours}h</span>
                      </div>
                      {budgetPercent !== null && (
                        <div
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            budgetPercent >= 90
                              ? 'bg-amber-500/10 text-amber-500'
                              : budgetPercent >= 70
                                ? 'bg-blue-500/10 text-blue-500'
                                : 'bg-emerald-500/10 text-emerald-500'
                          }`}
                        >
                          {budgetPercent}% {t('manager.ofBudget')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerOverview;
